import { DatePipe, formatDate } from "@angular/common";
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { Observable, tap } from 'rxjs';
import { AnnualStatement } from "@models/annual-statements/annual-statement";
import { DocumentSigning } from "@models/documents/document-signing";
import { AnnualStatementStatus, SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { IntegrationConnectionsEnum } from "@models/integrations/integration-connections.enum";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { FileLabelComponent } from "@shared/components/commons/file-label/file-label.component";
import { FileUploadComponent } from "@shared/controls/file-upload/file-upload.component";
import {
  MultipleInputEmailComponent
} from "@shared/controls/multiple-input-email/multiple-input-email.component";
import { NotificationComponent } from "@shared/components/commons/notification/notification.component";
import { EmailPreviewComponent } from "@shared/components/email-preview/email-preview.component";
import { SendByRadioComponent } from "@shared/components/documents-related/send-by-radio/send-by-radio.component";
import {
  SignDocumentsService
} from "../../../../../generate-documents/components/sign-documents/sign-documents.service";

@Component({
  selector: 'app-annual-sign-documents',
  standalone: true,
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    FormsModule,
    EmailPreviewComponent,
    MultipleInputEmailComponent,
    NotificationComponent,
    DatePickerComponent,
    FileUploadComponent,
    DatePipe,
    FileLabelComponent,
    SendByRadioComponent,
    CheckboxComponent,
    DisclaimerComponent,
    ListGridClientSideComponent
  ],
  templateUrl: 'annual-sign-documents.component.html',
  styleUrl: 'annual-sign-documents.component.scss',
  providers: [EmailsApiService],
})
export class AnnualSignDocumentsComponent extends SignDocumentsService implements OnInit, OnDestroy {
  @Input() annualStatementStatus: AnnualStatementStatus | undefined;
  @Input() isProviderConnectionStatusLoading = false;
  @Input() override selectedESignConnection: IntegrationConnectionsEnum | null = null;

  constructor() {
    super();
    this.toastr.toastrConfig.positionClass = 'toast-top-right';
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public disableHeaderBtn(value: boolean): void {
    this.annualStatementsService.disabledHeaderBtn.set(value);
  }

  override sendToESign(): Observable<DocumentSigning> {
    return super.sendToESign().pipe(
      tap(() => {
        const annualStatement = this.documentBase as AnnualStatement;
        if(annualStatement?.paymentDeadline && new Date(annualStatement.paymentDeadline) < new Date()){
          this.toastr.warning('No reminder was created because the payment deadline has passed. To set a reminder, click the \'Add Reminder\' button on the left side.');
        }
      }));
  }

  override showVoidEnvelope(value: boolean): void {
    value ? this.setEnvelopeGridConfig() : this.setGridConfig();
    this.annualStatementsService.showVoidEnvelope.set(value);
  }

  override showSendEmailBtn(value: boolean): void {
    this.annualStatementsService.showSendEmailBtn.set(value);
  }

  override showSendPaperBtn(value: boolean): void {
    this.annualStatementsService.showSendPaperBtn.set(value);
  }

  override sendSignLoading(value: boolean): void {
    this.annualStatementsService.sendSignLoading.set(value);
  }

  override getSendEmailNotification(): string {
    if (!this.documentBase.notificationDetails?.sentToEmails || this.documentBase.notificationDetails.sentToEmails.length === 0) {
      return '';
    }

    const emailList = this.documentBase.notificationDetails.sentToEmails.join(', </b><b>');
    const currentDate = formatDate(this.documentBase.notificationDetails?.sentOn ?? new Date(), 'dd MMM yyyy', 'en-US');
    return `The annual statement was successfully emailed to <b>${ emailList }</b> on <b>${ currentDate }</b>`;
  }

  get ShowVoidEnvelopeBtn(): boolean {
    return this.documentSigning?.signingStatus === SigningStatus.ReadyToSend
      || this.documentSigning?.signingStatus === SigningStatus.Created;
  }
}
